import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ArrowRight, SearchCheck, Layers, RefreshCw } from "lucide-react";
interface OptionItem {
  id: string;
  title: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  description: React.ReactNode;
  points: string[];
}
const options: OptionItem[] = [{
  id: "audit",
  title: "One-off SEO audit",
  icon: SearchCheck,
  description: <span>
        A clear diagnosis and prioritised roadmap before committing to ongoing work.
        Ideal for teams that can implement recommendations internally. See more
        about my{" "}
        <Link to="/SeoAuditsPage" className="text-primary underline font-medium hover:text-primary/80">
          SEO audits
        </Link>
        .
      </span>,
  points: ["Thorough evaluation of crawlability, indexation, and site architecture", "Prioritised remediation roadmap organised by commercial impact", "Clear guidance tailored for your in-house team or developers"]
}, {
  id: "project",
  title: "Project-based SEO consultancy",
  icon: Layers,
  description: "A structured engagement designed around major milestones, such as website redesigns or migrations, new product or service launches, or entering new local or national markets. Deliverables and timelines are agreed up front.",
  points: ["Agreed scope, key milestones, and written deliverables from day one", "Direct technical and content coordination with your in-house team", "Focused guidance to deliver high-priority search projects safely"]
}, {
  id: "ongoing",
  title: "Monthly SEO support",
  icon: RefreshCw,
  description: <span>
        Continuing freelance SEO services to steadily build and safeguard your organic search visibility through ongoing technical reviews, content planning, and regular progress reviews.{" "}
        <Link to="/MonthlySeoServicesPage" className="text-primary underline font-medium hover:text-primary/80">
          Read more about monthly SEO services
        </Link>
        .
      </span>,
  points: ["Regular technical health checks and structural recommendations", "Commercial content planning, briefs, and on-page optimisation", "Ongoing monitoring in Google Search Console and analytics, with clear next steps"]
}];
export default function ConsultancyOptions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="consultancy-options" aria-labelledby="consultancy-options-heading" ref={ref} className="bg-muted/30 py-20 lg:py-28 border-b border-border transition-opacity duration-700 ease-out">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`max-w-3xl mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="text-xs font-semibold tracking-wider uppercase text-primary mb-3">
            Engagement Formats
          </p>
          <h2 id="consultancy-options-heading" className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Flexible SEO Consultancy Options
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Choose the format that matches your current website stage and team
            capacity. Every engagement is structured around clear written priorities.
          </p>
        </div>

        {/* Three Service Mode Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-12">
          {options.map((opt, index) => {
          const Icon = opt.icon;
          return <div key={opt.id} className={`bg-card text-card-foreground border border-border rounded-xl p-6 sm:p-8 flex flex-col justify-between transition-all duration-500 hover:border-primary/40 hover:shadow-sm ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionDelay: `${index * 100}ms`
          }}>
                <div>
                  <div className="w-12 h-12 rounded-lg bg-secondary/60 text-primary flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 stroke-[1.75]" />
                  </div>

                  <h3 className="font-serif text-2xl font-semibold text-foreground mb-4 leading-snug">
                    {opt.title}
                  </h3>

                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
                    {opt.description}
                  </p>

                  <ul className="space-y-2.5 pt-4 border-t border-border/70 list-none p-0 m-0">
                    {opt.points.map((pt, pIdx) => <li key={pIdx} className="text-xs sm:text-sm text-foreground flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                        <span>{pt}</span>
                      </li>)}
                  </ul>
                </div>
              </div>;
        })}
        </div>

        {/* Tailored Quote Callout with Verbatim Text */}
        <div className={`bg-card text-card-foreground border border-border rounded-xl p-8 sm:p-10 transition-all duration-700 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="max-w-3xl">
            <p className="text-base sm:text-lg text-foreground font-medium leading-relaxed mb-6">
              I provide a bespoke quotation based on your website size and complexity, the competitiveness of your market, and the agreed scope of work. Scope and terms are agreed in writing before work begins, with no fixed public packages and no generic tiers.
            </p>

            <h3 className="font-serif text-lg sm:text-xl font-semibold text-foreground mb-4">
              What affects the cost of SEO consultancy
            </h3>

            <ul className="space-y-2.5 mb-6 list-none p-0 m-0">
              <li className="text-sm sm:text-base text-foreground flex items-start gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                <span>Website size and complexity (number of pages, ecommerce or brochure site)</span>
              </li>
              <li className="text-sm sm:text-base text-foreground flex items-start gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                <span>Current technical health and any previous SEO work</span>
              </li>
              <li className="text-sm sm:text-base text-foreground flex items-start gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                <span>How competitive your niche and target locations are (local, national or international)</span>
              </li>
              <li className="text-sm sm:text-base text-foreground flex items-start gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                <span>Scope of work (one-off audit, defined project, or ongoing monthly support)</span>
              </li>
              <li className="text-sm sm:text-base text-foreground flex items-start gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                <span>Delivery speed and how much your own team can implement</span>
              </li>
            </ul>

            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
              Audits and projects are quoted against an agreed scope, ongoing monthly consultancy is agreed in writing, and nothing starts before you have approved the written terms.
            </p>

            <div>
              <Button asChild size="lg" className="rounded-md">
                <Link to="/ContactPage">
                  Request a Tailored SEO Quote
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}